import { DOCUMENT_TAGS } from './constants';

/** Minimal shape from application financial / bank uploads */
export type FinancialFileLike = {
  tags?: string | null;
  documentId?: string | null;
};

/** GraphQL digitization fragment on financial `files`. */
export type FinancialFileDigitizationFields = FinancialFileLike & {
  digitization?: {
    batchId?: number | null;
    status?: string | null;
    rejectionReasons?: string[] | null;
  } | null;
};

export const CLIENT_DIGITIZATION_FAILED = 'Failed';

export type DigitizationCategory =
  | 'bank'
  | 'audited'
  | 'provisional'
  | 'coi'
  | 'tax_returns';

/** GraphQL / upload `documentId` can be string; REST expects numeric IDs. */
export const documentsToNumericIds = (
  docs: { documentId?: string | null }[]
): number[] => {
  const ids: number[] = [];
  docs.forEach((d) => {
    if (d.documentId == null || d.documentId === '') return;
    const n = parseInt(String(d.documentId), 10);
    if (!Number.isNaN(n)) ids.push(n);
  });
  return ids;
};

export const isGatePassingStatus = (status?: string | null): boolean => {
  if (status == null) return false;
  const s = String(status).trim();
  const lc = s.toLowerCase();
  return lc === 'verified' || lc === 'completed';
};

/** Stop polling when backend returns a terminal review outcome. */
export const isTerminalApiDigitizationStatus = (status: string): boolean => {
  const lc = String(status).trim().toLowerCase();
  return (
    lc === 'verified' ||
    lc === 'completed' ||
    lc === 'validation failed' ||
    lc === 'failed'
  );
};

export const isValidationFailedStatus = (status?: string | null): boolean => {
  if (status == null) return false;
  return String(status).trim().toLowerCase() === 'validation failed';
};

export const inferCategoryFromFileTag = (
  tag?: string | null
): DigitizationCategory | null => {
  if (!tag) return null;
  if (tag === DOCUMENT_TAGS.COI) return 'coi';
  if (
    tag === DOCUMENT_TAGS.FINANCIALS ||
    tag === DOCUMENT_TAGS.PROFIT_AND_LOSS ||
    tag === DOCUMENT_TAGS.BALANCE_SHEET
  ) {
    return 'audited';
  }
  if (
    tag === DOCUMENT_TAGS.PROVISIONAL_FINANCIALS ||
    tag === DOCUMENT_TAGS.PROVISIONAL_PL_STATEMENT ||
    tag === DOCUMENT_TAGS.PROVISIONAL_BALANCE_SHEET
  ) {
    return 'provisional';
  }
  if (tag === DOCUMENT_TAGS.TAX_RETURNS) return 'tax_returns';
  return null;
};

/** Categories that have ≥1 uploaded financial file (excludes ageing/debt). */
export const financialBucketsWithUploads = (
  files: FinancialFileLike[]
): DigitizationCategory[] => {
  const set = new Set<DigitizationCategory>();
  (files || []).forEach((f) => {
    const c = inferCategoryFromFileTag(f.tags ?? undefined);
    if (c) set.add(c);
  });
  return Array.from(set);
};

export const financialDigitizationBucketsSatisfiedFromFiles = (
  files: FinancialFileDigitizationFields[]
): boolean => {
  const buckets = financialBucketsWithUploads(files);
  for (let i = 0; i < buckets.length; i++) {
    const b = buckets[i];
    const bucketSatisfied = (files || []).some(
      (f) =>
        inferCategoryFromFileTag(f.tags ?? undefined) === b &&
        isGatePassingStatus(f.digitization?.status)
    );
    if (!bucketSatisfied) return false;
  }
  return true;
};

export const requiresEinForCategory = (cat: DigitizationCategory): boolean =>
  cat === 'coi' || cat === 'audited' || cat === 'tax_returns';

export const financialBucketsNeedingEinPresent = (
  files: FinancialFileLike[]
): boolean => {
  return financialBucketsWithUploads(files).some((c) =>
    requiresEinForCategory(c)
  );
};

/** Parse FY2024 / FY2024 and FY2025 → earliest & latest calendar years as strings */
export const parseAuditedYearsFromFYLabel = (label: string) => {
  const years =
    label.match(/\b(19|20)\d{2}\b/g)?.map((y) => y) ?? ([] as string[]);
  if (!years.length) {
    const y = String(new Date().getFullYear());
    return { expected_start_year: y, expected_end_year: y };
  }
  if (years.length === 1) {
    const y = years[0];
    return { expected_start_year: y, expected_end_year: y };
  }
  const sorted = [...years].sort((a, b) => Number(a) - Number(b));
  return {
    expected_start_year: sorted[0],
    expected_end_year: sorted[sorted.length - 1],
  };
};

export const auditedDocumentIdsFromFiles = (
  files: FinancialFileLike[]
): number[] =>
  documentsToNumericIds(
    (files || []).filter(
      (f) =>
        f.tags === DOCUMENT_TAGS.FINANCIALS ||
        f.tags === DOCUMENT_TAGS.PROFIT_AND_LOSS ||
        f.tags === DOCUMENT_TAGS.BALANCE_SHEET
    )
  );

export const provisionalDocumentIdsFromFiles = (
  files: FinancialFileLike[]
): number[] =>
  documentsToNumericIds(
    (files || []).filter(
      (f) =>
        f.tags === DOCUMENT_TAGS.PROVISIONAL_FINANCIALS ||
        f.tags === DOCUMENT_TAGS.PROVISIONAL_PL_STATEMENT ||
        f.tags === DOCUMENT_TAGS.PROVISIONAL_BALANCE_SHEET
    )
  );

/** True if user satisfied audited bucket: `financials` tag and/or legacy P&L + balance sheet pair. */
export const hasAuditedFinancialsBucket = (
  files: FinancialFileLike[]
): boolean => {
  if (!files?.length) return false;
  if (files.some((f) => f.tags === DOCUMENT_TAGS.FINANCIALS)) return true;
  const hasPl = files.some((f) => f.tags === DOCUMENT_TAGS.PROFIT_AND_LOSS);
  const hasBs = files.some((f) => f.tags === DOCUMENT_TAGS.BALANCE_SHEET);
  return hasPl && hasBs;
};

/** True if user satisfied provisional bucket: `provisional_financials` and/or legacy pair. */
export const hasProvisionalFinancialsBucket = (
  files: FinancialFileLike[]
): boolean => {
  if (!files?.length) return false;
  if (files.some((f) => f.tags === DOCUMENT_TAGS.PROVISIONAL_FINANCIALS))
    return true;
  const hasPl = files.some(
    (f) => f.tags === DOCUMENT_TAGS.PROVISIONAL_PL_STATEMENT
  );
  const hasBs = files.some(
    (f) => f.tags === DOCUMENT_TAGS.PROVISIONAL_BALANCE_SHEET
  );
  return hasPl && hasBs;
};

export const mergeUniqueIds = (a: number[], b: number[]): number[] =>
  Array.from(new Set([...a, ...b]));
